import { Button } from '@repo/ui/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/components/ui/card';
import { Badge } from '@repo/ui/components/ui/badge';
import { CheckCircle, Circle, LayoutGrid, List } from 'lucide-react';
import { Step } from '../types';

interface StepNavigationProps {
  steps: Step[];
  currentStep: number;
  completedSteps: Set<number>;
  onStepClick: (stepIndex: number) => void;
  viewMode: 'horizontal' | 'vertical';
  onViewModeChange: (mode: 'horizontal' | 'vertical') => void;
}

export function StepNavigation({
  steps,
  currentStep,
  completedSteps,
  onStepClick,
  viewMode,
  onViewModeChange
}: StepNavigationProps) {
  if (viewMode === 'vertical') {
    return (
      <Card className="w-72 border-border/50 bg-card shadow-sm ring-1 ring-border/20 dark:border-border dark:ring-border/30">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold text-foreground">Étapes</CardTitle>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onViewModeChange('horizontal')}
                className="h-8 w-8 border-border/50 bg-background p-0 hover:bg-accent dark:border-border dark:hover:bg-accent"
                aria-label="Vue horizontale"
              >
                <LayoutGrid className="h-4 w-4" aria-hidden="true" />
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={() => onViewModeChange('vertical')}
                className="h-8 w-8 bg-primary p-0 text-primary-foreground hover:bg-primary dark:bg-primary dark:hover:bg-primary"
                aria-label="Vue verticale"
              >
                <List className="h-4 w-4" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {steps.map((step, index) => {
            const isCurrent = index === currentStep;
            const isCompleted = completedSteps.has(index);

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => onStepClick(index)}
                className={`group flex w-full items-center gap-3 rounded-xl border p-4 text-left transition-all duration-200 ${isCurrent
                  ? 'border-primary/50 bg-primary/10 ring-2 ring-primary/20 shadow-sm dark:border-primary/50 dark:bg-primary/20 dark:ring-primary/30'
                  : isCompleted
                    ? 'border-border/50 bg-primary/5 hover:border-primary/30 hover:bg-primary/10 hover:shadow-sm dark:border-border dark:bg-primary/10 dark:hover:bg-primary/20'
                    : 'border-border/50 bg-card hover:border-border hover:bg-accent/50 hover:shadow-sm dark:border-border dark:hover:bg-accent'
                  }`}
                aria-label={`Étape ${index + 1}: ${step.title}`}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <div
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isCurrent
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary/20 dark:ring-primary/30'
                    : isCompleted
                      ? 'bg-primary text-primary-foreground dark:bg-primary'
                      : 'bg-muted text-muted-foreground ring-1 ring-border/20 dark:ring-border/30'
                    }`}
                >

                </div>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span
                      className={`text-sm font-semibold ${isCurrent ? 'text-foreground' : 'text-foreground'
                        }`}
                    >
                      {index + 1}. {step.title}
                    </span>


                  </div>
                  <p className="text-xs font-medium text-muted-foreground line-clamp-2">
                    {step.description}
                  </p>
                </div>
              </button>
            );
          })}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/50 bg-card shadow-sm ring-1 ring-border/20 dark:border-border dark:ring-border/30">
      <CardContent className="p-4">
        <div className="mb-4 flex items-center justify-between">
          <CardTitle className="text-base font-semibold text-foreground">Étapes du formulaire</CardTitle>
          <div className="flex gap-2">
            <Button
              variant="default"
              size="sm"
              onClick={() => onViewModeChange('horizontal')}
              className="h-8 w-8 bg-primary p-0 text-primary-foreground hover:bg-primary dark:bg-primary dark:hover:bg-primary"
              aria-label="Vue horizontale"
            >
              <LayoutGrid className="h-4 w-4" aria-hidden="true" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onViewModeChange('vertical')}
              className="h-8 w-8 border-border/50 bg-background p-0 hover:bg-accent dark:border-border dark:hover:bg-accent"
              aria-label="Vue verticale"
            >
              <List className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-thin">
          {steps.map((step, index) => {
            const isCurrent = index === currentStep;
            const isCompleted = completedSteps.has(index);

            return (
              <button
                key={step.id}
                type="button"
                onClick={() => onStepClick(index)}
                className={`group flex min-w-[140px] flex-col items-center gap-2 rounded-xl border p-3 transition-all duration-200 ${isCurrent
                  ? 'border-primary/50 bg-primary/10 ring-2 ring-primary/20 shadow-sm dark:border-primary/50 dark:bg-primary/20 dark:ring-primary/30'
                  : isCompleted
                    ? 'border-border/50 bg-primary/5 hover:border-primary/30 hover:bg-primary/10 hover:shadow-sm dark:border-border dark:bg-primary/10 dark:hover:bg-primary/20'
                    : 'border-border/50 bg-card hover:border-border hover:bg-accent/50 hover:shadow-sm dark:border-border dark:hover:bg-accent'
                  }`}
                aria-label={`Étape ${index + 1}: ${step.title}`}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-lg ${isCurrent
                    ? 'bg-primary text-primary-foreground ring-2 ring-primary/20 dark:ring-primary/30'
                    : isCompleted
                      ? 'bg-primary text-primary-foreground dark:bg-primary'
                      : 'bg-muted text-muted-foreground ring-1 ring-border/20 dark:ring-border/30'
                    }`}
                >
                  {isCompleted ? (
                    <CheckCircle className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Circle className="h-4 w-4" aria-hidden="true" />
                  )}
                </div>
                <div className="flex w-full flex-col items-center gap-1.5 text-center">
                  <span
                    className={`line-clamp-2 text-xs font-semibold leading-tight ${isCurrent ? 'text-foreground' : 'text-foreground'
                      }`}
                  >
                    {index + 1}. {step.title}
                  </span>

                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
